import { ChevronDown, FileText, LogOut, Menu, Monitor, Moon, Search, Sun, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Kbd } from '@/components/ui/kbd'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useTheme } from './ThemeProvider'

export function Topbar({ onSearch, onMenu }: { onSearch: () => void; onMenu: () => void }) {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const name = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : ''
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor

  return (
    <div className="sticky top-0 z-30 bg-gradient-to-b from-background via-background/90 to-transparent px-3 pt-3">
    <header className="flex h-14 items-center gap-2 rounded-2xl sm:gap-3 border bg-card/85 px-3 shadow-[0_1px_2px_color-mix(in_oklab,var(--shadow-soft)_4%,transparent),0_12px_32px_-12px_color-mix(in_oklab,var(--shadow-color)_18%,transparent)] backdrop-blur-xl dark:shadow-[0_12px_32px_-12px_color-mix(in_oklab,var(--black)_60%,transparent)]">
      <Button variant="ghost" size="icon" className="rounded-xl lg:hidden" onClick={onMenu} aria-label={t('common.menu')}>
        <Menu />
      </Button>
      <button
        type="button"
        onClick={onSearch}
        className="group flex h-9 w-full min-w-0 max-w-md items-center gap-2.5 rounded-xl border border-transparent bg-muted/70 px-3 text-sm text-muted-foreground transition-all hover:border-border hover:bg-card hover:shadow-sm"
      >
        <Search className="size-4 transition-colors group-hover:text-brand-blue dark:group-hover:text-brand-sky" />
        <span className="flex-1 truncate text-left">{t('common.searchPlaceholder')}</span>
        <Kbd className="hidden bg-card sm:inline-flex">⌘K</Kbd>
      </button>
      <div className="ml-auto flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-xl text-muted-foreground hover:text-foreground" aria-label={t('common.themeToggle')}>
              <ThemeIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setTheme('light')}><Sun />{t('common.theme.light')}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTheme('dark')}><Moon />{t('common.theme.dark')}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTheme('system')}><Monitor />{t('common.theme.system')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <NotificationBell />
        <span className="mx-1.5 hidden h-6 w-px bg-border sm:block" aria-hidden />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2.5 rounded-xl p-1 transition-colors hover:bg-muted md:pr-2.5"
              aria-label={name}
            >
              <Avatar name={name || '?'} src={user?.avatar_url} />
              <span className="hidden min-w-0 text-left leading-tight md:block">
                <span className="block max-w-36 truncate text-sm font-medium text-foreground">{name}</span>
                <span className="block max-w-36 truncate text-[11px] text-muted-foreground">{user?.roles[0]?.name ?? user?.email}</span>
              </span>
              <ChevronDown className="hidden size-3.5 text-muted-foreground md:block" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="text-sm font-medium text-foreground">{name}</div>
              <div className="truncate">{user?.email}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {user?.roles.map((r) => <span key={r.id} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{r.name}</span>)}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/account">
                <UserRound />
                {t('auth.profile')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/me/documents">
                <FileText />
                {t('me.documents.title')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void logout()} className="text-destructive [&_svg]:text-destructive"><LogOut />{t('auth.signOut')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
    </div>
  )
}
